import { Fragment, useCallback, useContext } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import NavBarListItem from 'components/NavKit/NavBarListItem'
import NavBarListItemDivider from 'components/NavKit/NavBarListItemDivider'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import useMerchandiseCategoriesList from 'hooks/Merchandise/useMerchandiseCategoriesList'
import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import ModalContext from 'contexts/ModalContext'
import { getMerchandiseCategorySearchUrl, MIN_SUBCATEGORIES } from '../merchandiseUtils'
import MerchandiseSubcategoryDrawer from './MerchandiseSubcategoryDrawer'
import IconPlaceholder from './components/IconPlaceholder'
import { useDrawerTracking, useMegaMenuTracking } from 'analytics/snowplow/events/megaMenuEvents'
const CategoriesSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-inline: ${rem(8)};
`

const StyledNavBarListItem = styled(NavBarListItem)`
  padding-block: ${rem(12)};
  gap: ${rem(8)};
`

function MerchandiseCategoryDrawer() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { onClose } = useDrawerTracking('category')
  const trackClick = useMegaMenuTracking('category')

  const [categoryIds] = useMerchandiseCategoriesList({ level: 0, limit: 10, sortBy: 'top' })
  const [categories] = useMerchandiseCategories({
    categoryIds: categoryIds,
  }, { disabled: !categoryIds || categoryIds.length === 0 })

  const handleCategoryWithSubcategoriesClick = useCallback((category: App.MerchandiseCategory, index: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    trackClick(category, index)
    showModal(<MerchandiseSubcategoryDrawer category={category} onCloseAll={onClose} />)
  }, [showModal, onClose, trackClick])

  const handleCategoryWithoutSubcategoriesClick = useCallback((category: App.MerchandiseCategory, index: number) => () => {
    trackClick(category, index)
    onClose()
  }, [onClose, trackClick])

  return (
    <ModalBase mode="drawer">
      <ModalHeader
        title={intl.formatMessage({
          id: 'K2FHsBkyXVWIXaCnI42Y',
          defaultMessage: 'Shop by Category',
          description: 'Title for merchandise category drawer in the merchandise shop section',
        })}
      />
      <ModalBody>
        <ModalContent flush>
          <CategoriesSection>
            {categories.map((category, index) => {
              const hasSubcategories = category.childCategoryIds.length >= MIN_SUBCATEGORIES

              return (
                <Fragment key={category.id}>
                  <StyledNavBarListItem
                    title={category.name}
                    to={hasSubcategories ? undefined : getMerchandiseCategorySearchUrl(category)}
                    onClick={hasSubcategories
                      ? handleCategoryWithSubcategoriesClick(category, index)
                      : handleCategoryWithoutSubcategoriesClick(category, index)}
                    endIcon={hasSubcategories ? <LineAngleRightIcon size={24} /> : <IconPlaceholder size={24} />}
                  />
                  <NavBarListItemDivider />
                </Fragment>
              )
            })}
          </CategoriesSection>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default MerchandiseCategoryDrawer
